import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Trash2 } from "lucide-react";

import PhotoPlus from "@/assets/icons/authentication/PhotoPlusOrange.svg";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import { updateV2FieldKeyPair } from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import type { FileDetails } from "@/types/types";

type Props = {
  maxSize?: number; // default 5MB
  accept?: string; // default image/*
  useLiveUrl?: boolean; // if you want to preview live_url after upload
};

const ProfilePhotoUploader: React.FC<Props> = ({
  maxSize = 5 * 1024 * 1024,
  accept = "image/jpeg,image/png,image/webp",
  useLiveUrl = false,
}) => {
  const dispatch = useAppDispatch();

  const profilePhoto = useAppSelector((s) => s.signUpV2.v2Extras.profilePhoto);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // preview for local selection
  useEffect(() => {
    if (!selectedFile) {
      setPreview(null);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const openPicker = () => fileInputRef.current?.click();

  const removeAll = () => {
    setSelectedFile(null);
    setPreview(null);

    dispatch(
      updateV2FieldKeyPair({
        key: "profilePhoto",
        value: null,
      }),
    );
  };

  const onSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file || isUploading) return;

    // reset input value so selecting same file again works
    e.target.value = "";

    // validations
    if (file.size > maxSize) {
      toast.error(
        `File too large. Max ${(maxSize / 1024 / 1024).toFixed(0)}MB`,
      );
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please select a JPG, PNG or WebP image.");
      return;
    }

    setSelectedFile(file);

    // ✅ immediately set redux with local preview for UI consistency if you want
    const now = Date.now();
    const sanitize = (name: string) =>
      name.replace(/[^a-zA-Z0-9.\-_]/g, "_").slice(0, 100);

    const draft: FileDetails = {
      name: `${now}-${sanitize(file.name)}`,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      webkitRelativePath: (file as any).webkitRelativePath ?? "",
      local_url: URL.createObjectURL(file),
      live_url: null,
      file, // ✅ important
    };

    dispatch(
      updateV2FieldKeyPair({
        key: "profilePhoto",
        value: draft,
      }),
    );

    // auto upload instantly (smooth UX)
    await uploadToS3(draft);
  };

  const uploadToS3 = async (draft: FileDetails) => {
    try {
      setIsUploading(true);

      // your util should upload using draft.file if needed
      const uploaded = await UploadFileToS3(draft);

      const updated: FileDetails = {
        ...uploaded,
        local_url: uploaded.live_url as string,
      };

      dispatch(
        updateV2FieldKeyPair({
          key: "profilePhoto",
          value: updated,
        }),
      );

      toast.success("Profile photo uploaded!");
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to upload photo. Try again.",
      );
      // rollback
      dispatch(
        updateV2FieldKeyPair({
          key: "profilePhoto",
          value: profilePhoto?.live_url ? profilePhoto : null,
        }),
      );
      setSelectedFile(null);
      setPreview(null);
    } finally {
      URL.revokeObjectURL(draft.local_url);
      setIsUploading(false);
    }
  };

  const hasPhoto = !!profilePhoto || !!preview;

  const imgSrc =
    profilePhoto?.live_url && useLiveUrl
      ? profilePhoto.live_url
      : preview || profilePhoto?.local_url || profilePhoto?.live_url || "";

  return (
    <div className="mt-3">
      {/* Hidden input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={onSelectFile}
      />

      {/* Upload box (only when no photo) */}
      {!hasPhoto && (
        <button
          type="button"
          onClick={openPicker}
          className="bg-[#FFFAF6] p-2 flex items-center gap-x-[9px] w-fit cursor-pointer select-none"
        >
          <div className="size-[60px] rounded-full border border-[#F4F4F4] flex items-center justify-center bg-white">
            <PhotoPlus />
          </div>

          <div className="text-left">
            <h6 className="text-[#121217] text-xs tracking-[-1%] font-medium">
              Upload profile photo
            </h6>
            <p className="text-[#475467] text-[7px] tracking-[-1.7%] mt-1">
              Allowed images • Max {(maxSize / 1024 / 1024).toFixed(0)}MB
            </p>
          </div>
        </button>
      )}

      {/* Preview */}
      {hasPhoto && (
        <div className="mt-3">
          <div
            className="relative bg-[#F8F8F8] border border-[#EAECF0] rounded-[8px] overflow-hidden flex justify-center items-center"
            style={{ width: "124px", height: "124px" }}
          >
            {!!imgSrc && (
              <img
                src={imgSrc}
                alt={profilePhoto?.name ?? "profile"}
                className="object-cover w-full h-full"
              />
            )}

            {/* Upload overlay */}
            {isUploading && (
              <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center">
                <div className="h-5 w-5 rounded-full border-2 border-[#FF6600] border-t-transparent animate-spin" />
                <p className="mt-2 text-[10px] font-medium text-[#475467]">
                  Uploading...
                </p>
              </div>
            )}

            {/* Delete */}
            <button
              type="button"
              className="absolute top-2 right-2 bg-white p-1 rounded-md shadow-sm"
              onClick={removeAll}
              aria-label="Remove photo"
              disabled={isUploading}
            >
              <Trash2 size={20} className="text-red-500" />
            </button>
          </div>

          {/* Change button */}
          <button
            type="button"
            onClick={openPicker}
            className="mt-2 text-[11px] font-semibold text-[#FF6600]"
            disabled={isUploading}
          >
            Change photo
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfilePhotoUploader;
